import { useState } from 'react';
import type { ReactNode } from 'react';
import { Drawer } from 'antd';
import {
  CheckCircleFilled,
  DeleteOutlined,
  FileTextOutlined,
  PushpinFilled,
  PushpinOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';

import { MenuThaoTac } from '../../../components/shared';
import { Badge, Button, Empty, Separator, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useRoutePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { dungLuongVN, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { STATUS_CONFIG } from '../../xu-ly/constants';
import { XemBanChep } from '../../xu-ly/components/XemBanChep';
import { XemTruocTep } from '../../xu-ly/components/XemTruocTep';
import { useTenNguoi } from '../../xu-ly/hooks/useTenNguoi';
import { NGUON_BAN, TRANG_THAI_BAN } from '../constants';
import { useBanChepKhoOcr, useChiTietKhoOcr, useGhiKhoOcr, useTepKhoOcr } from '../hooks/useKhoOcr';
import { dangDoc, phanTramDoc } from '../nghiep-vu/kho-ocr';
import type { OcrFile, OcrVersion } from '../types';

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-content-muted">{nhan}</dt>
      <dd className="mt-0.5 break-words text-sm text-content-primary">{children}</dd>
    </div>
  );
}

function Phan({ tieuDe, phai, children }: { tieuDe: string; phai?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-content-primary">{tieuDe}</h3>
        {phai}
      </div>
      {children}
    </section>
  );
}

type HopBan = { loai: 'xoa' | 'huy'; ban: OcrVersion } | null;

/**
 * Ngăn chi tiết một tài liệu trong kho: thông tin tệp, xem trước, các bản OCR và bản chép.
 *
 * Ngăn bên phải thay vì màn riêng: người dùng mở để ghim hay xoá một bản rồi quay lại danh sách
 * ngay, giữ nguyên bộ lọc và vị trí cuộn — cùng tiền lệ ngăn chi tiết của Nhật ký thao tác.
 * Đang có lượt đọc thì `useChiTietKhoOcr` tự hỏi lại, tiến độ nhích theo worker.
 */
export function NganChiTietTep({
  id,
  onClose,
  onDocLai,
}: {
  id: string | null;
  onClose: () => void;
  onDocLai: (tep: OcrFile) => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const dieuHuong = useDieuHuong();
  const tenNguoi = useTenNguoi();
  const coTao = useRoutePermission('CREATE');
  const coSua = useRoutePermission('UPDATE');
  const coXoa = useRoutePermission('DELETE');
  const q = useChiTietKhoOcr(id ?? undefined);
  const tep = useTepKhoOcr(id ?? undefined, id !== null);
  const ghi = useGhiKhoOcr();
  const [banChep, setBanChep] = useState<OcrVersion | null>(null);
  const [hop, setHop] = useState<HopBan>(null);
  const banChepQ = useBanChepKhoOcr(id ?? undefined, banChep?.id);
  const f = q.data;

  const baoLoi = (err: unknown) => toast.error(dichLoi(err));

  const dongHop = () => {
    setHop(null);
    ghi.xoaBan.reset();
    ghi.huyDoc.reset();
  };

  const xacNhanHop = () => {
    if (!hop || !f) return;
    const du = { id: f.id, versionId: hop.ban.id };
    if (hop.loai === 'xoa') {
      ghi.xoaBan.mutate(du, {
        onSuccess: () => {
          toast.success(t('khoOcr.daXoaBan'));
          if (banChep?.id === hop.ban.id) setBanChep(null);
          setHop(null);
        },
      });
    } else {
      ghi.huyDoc.mutate(du, {
        onSuccess: () => {
          toast.success(t('khoOcr.daHuyDoc'));
          setHop(null);
        },
      });
    }
  };

  const tenBan = (v: OcrVersion) => (v.is_text_layer ? t('ocr.lopVanBanPdf') : (v.reader_label ?? v.reader_key ?? ''));

  const theBan = (v: OcrVersion) => {
    const pt = phanTramDoc(v);
    const chay = dangDoc(v);
    return (
      <li key={v.id} className="rounded-lg border border-default p-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 truncate text-sm font-medium text-content-primary">{tenBan(v)}</span>
              <Badge variant={TRANG_THAI_BAN[v.status].variant}>{t(TRANG_THAI_BAN[v.status].khoa)}</Badge>
              {v.is_effective && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-success-700">
                  <CheckCircleFilled aria-hidden /> {t('khoOcr.dangDung')}
                </span>
              )}
              {v.pinned_at && (
                <span className="inline-flex items-center gap-1 text-xs text-content-secondary">
                  <PushpinFilled aria-hidden /> {t('khoOcr.daGhim')}
                </span>
              )}
            </div>
            <p className="text-xs text-content-muted">
              {t(NGUON_BAN[v.origin])} ·{' '}
              {t('khoOcr.banTao', { ngay: ngayGioVN(v.created_at, locale), nguoi: tenNguoi(v.created_by) ?? '' })}
              {v.page_count ? ` · ${t('ocr.soTrang', { so: soVN(v.page_count, locale) })}` : ''}
            </p>
            {chay && (
              <div className="pt-1">
                <div className="h-1 overflow-hidden rounded-full bg-subtle">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${pt ?? 5}%` }} />
                </div>
                <p className="mt-1 text-2xs text-content-muted">
                  {v.page_count
                    ? t('khoOcr.tienDo', { xong: v.pages_done ?? 0, tong: v.page_count })
                    : t('khoOcr.tienDoChuaRo')}
                </p>
              </div>
            )}
            {v.status === 'FAILED' && v.error && <p className="text-xs text-danger-700">{v.error}</p>}
            {v.attached_count > 0 && (
              <div className="pt-1">
                <p className="text-xs text-content-secondary">
                  {t('khoOcr.soChungTuDung', { so: soVN(v.attached_count, locale) })}
                </p>
                {(v.documents ?? []).length > 0 && (
                  <ul className="mt-1 space-y-1">
                    {(v.documents ?? []).map((d) => (
                      <li key={d.id} className="flex min-w-0 items-center gap-2">
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto min-w-0 p-0"
                          onClick={() => dieuHuong(`/xu-ly/chung-tu/${d.id}`)}
                        >
                          <span className="truncate">{d.schema_name}</span>
                        </Button>
                        <span className="min-w-0 truncate text-2xs text-content-muted">{d.file_name}</span>
                        <Badge variant={STATUS_CONFIG[d.status].variant}>{t(STATUS_CONFIG[d.status].khoa)}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <MenuThaoTac
            tenBanGhi={tenBan(v)}
            muc={[
              {
                khoa: 'ban-chep',
                nhan: t('khoOcr.xemBanChep'),
                icon: <FileTextOutlined />,
                nhom: 'xem',
                hien: v.status === 'DONE',
                onChon: () => setBanChep(v),
              },
              {
                khoa: 'ghim',
                nhan: t('khoOcr.ghim'),
                icon: <PushpinOutlined />,
                hien: coSua && v.status === 'DONE' && !v.pinned_at,
                onChon: () =>
                  ghi.ghim.mutate({ id: f!.id, versionId: v.id }, { onSuccess: () => toast.success(t('khoOcr.daGhimToast')), onError: baoLoi }),
              },
              {
                khoa: 'bo-ghim',
                nhan: t('khoOcr.boGhim'),
                icon: <PushpinOutlined />,
                hien: coSua && Boolean(v.pinned_at),
                onChon: () =>
                  ghi.boGhim.mutate({ id: f!.id, versionId: v.id }, { onSuccess: () => toast.success(t('khoOcr.daBoGhim')), onError: baoLoi }),
              },
              {
                khoa: 'huy',
                nhan: t('khoOcr.huyDoc'),
                icon: <StopOutlined />,
                hien: coSua && chay,
                onChon: () => setHop({ loai: 'huy', ban: v }),
              },
              {
                khoa: 'xoa',
                nhan: t('khoOcr.xoaBanNut'),
                icon: <DeleteOutlined />,
                nhom: 'pha-huy',
                hien: coXoa,
                voHieu: Boolean(v.delete_blocked_reason),
                moTa: v.delete_blocked_reason ?? undefined,
                onChon: () => setHop({ loai: 'xoa', ban: v }),
              },
            ]}
          />
        </div>
      </li>
    );
  };

  return (
    <Drawer
      open={id !== null}
      onClose={onClose}
      title={f?.file_name ?? t('khoOcr.chiTietTieuDe')}
      placement="right"
      size="min(760px, 100vw)"
      destroyOnHidden
      extra={
        f && coTao ? (
          <Button
            variant="outline"
            size="sm"
            icon={<ReloadOutlined />}
            disabled={Boolean(f.running)}
            onClick={() => onDocLai(f)}
          >
            {t('khoOcr.docLai')}
          </Button>
        ) : undefined
      }
    >
      {q.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : q.error && !f ? (
        <div role="alert" className="flex flex-col items-start gap-3">
          <p className="text-sm text-danger-700">{t('khoOcr.loiChiTiet', { loi: dichLoi(q.error) })}</p>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : f ? (
        <div className="space-y-5">
          <Phan tieuDe={t('khoOcr.thongTin')}>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Dong nhan={t('khoOcr.tenTep')}>{f.file_name ?? t('chung.khongCo')}</Dong>
              <Dong nhan={t('khoOcr.kichThuoc')}>{dungLuongVN(f.file_size, locale)}</Dong>
              <Dong nhan={t('khoOcr.kieuTep')}>
                <span className="font-mono text-xs">{f.mime_type ?? t('chung.khongCo')}</span>
              </Dong>
              <Dong nhan={t('khoOcr.cot.nguoiTai')}>
                {t('khoOcr.banTao', { ngay: ngayGioVN(f.created_at, locale), nguoi: tenNguoi(f.created_by) ?? '' })}
              </Dong>
            </dl>
          </Phan>

          <Separator />

          <Phan tieuDe={t('khoOcr.xemTruoc')}>
            <div className="h-[360px] overflow-hidden rounded-lg border border-default bg-subtle">
              <XemTruocTep
                tep={tep.data}
                dangTai={tep.isLoading}
                loi={tep.error}
                onThuLai={() => void tep.refetch()}
                tenTep={f.file_name}
                mime={f.mime_type}
              />
            </div>
          </Phan>

          <Separator />

          <Phan tieuDe={t('khoOcr.cacBan', { so: soVN(f.versions.length, locale) })}>
            {f.versions.length === 0 ? (
              <Empty icon={FileTextOutlined} title={t('khoOcr.chuaCoBan')} description={t('khoOcr.chuaCoBanMo')} />
            ) : (
              <ul className="space-y-2">{f.versions.map(theBan)}</ul>
            )}
          </Phan>

          {banChep && (
            <>
              <Separator />
              <Phan
                tieuDe={t('khoOcr.banChepCua', { ten: tenBan(banChep) })}
                phai={
                  <Button variant="ghost" size="sm" onClick={() => setBanChep(null)}>
                    {t('khoOcr.dongBanChep')}
                  </Button>
                }
              >
                <div className="rounded-lg border border-default">
                  <XemBanChep
                    banChep={banChepQ.data}
                    dangTai={banChepQ.isLoading}
                    loi={banChepQ.error}
                    onThuLai={() => void banChepQ.refetch()}
                  />
                </div>
              </Phan>
            </>
          )}
        </div>
      ) : null}

      <HopXacNhanThaoTac
        open={hop !== null}
        tieuDe={hop?.loai === 'huy' ? t('khoOcr.hopHuyDocTieuDe') : t('khoOcr.xoaBanTieuDe')}
        than={
          hop?.loai === 'huy'
            ? t('khoOcr.hopHuyDocMo')
            : t('khoOcr.xoaBanMo', { ten: hop ? tenBan(hop.ban) : '' })
        }
        nhanXacNhan={hop?.loai === 'huy' ? t('khoOcr.huyDoc') : t('khoOcr.xoaBanNut')}
        nguyHiem={hop?.loai === 'xoa'}
        dangChay={ghi.xoaBan.isPending || ghi.huyDoc.isPending}
        loi={
          hop?.loai === 'xoa' && ghi.xoaBan.error
            ? dichLoi(ghi.xoaBan.error)
            : hop?.loai === 'huy' && ghi.huyDoc.error
              ? dichLoi(ghi.huyDoc.error)
              : null
        }
        onClose={dongHop}
        onXacNhan={xacNhanHop}
      />
    </Drawer>
  );
}
